import { Box, Button } from "@mui/material"
import { Upload } from "lucide-react"
import { useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { PageHeader } from "@/components/ui/page-header"
import { useToast } from "@/components/ui/use-toast"
import { PurchaseOrderDialog } from "@crm-src/components/purchase-orders/purchase-order-dialog"
import { PurchaseOrdersList } from "@crm-src/components/purchase-orders/purchase-orders-list"

/**
 * CRM ▸ Sales ▸ Purchase Orders (`/crm/purchase-orders`).
 *
 * Every purchase order a customer or reseller sent XOP, whichever way it came
 * in: generated when a reseller accepted a quote, uploaded by the reseller on
 * its portal, or uploaded here by staff from an email. Behind the financial
 * route guard like Quotes and Invoices (`CrmRoutes.tsx`).
 *
 * One header action: **Upload purchase order** for a holder of `crm:edit`
 * (what crm-api's create checks). Who is emailed about purchase orders is
 * System Admin ▸ System Emails — deliberately NOT linked from here (CEO,
 * 2026-09-17). See docs/crm.md → "Purchase Orders".
 */
export default function PurchaseOrdersPage(): JSX.Element {
  const navigate = useNavigate()
  const { toast } = useToast()
  const canEdit = useCan("crm:edit")
  const [uploadOpen, setUploadOpen] = useState(false)

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }} data-cy="purchase-orders-page">
      <PageHeader
        title="Purchase Orders"
        description="Purchase orders customers and resellers sent to XOP"
        actions={
          <>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Upload size={16} />}
                onClick={() => setUploadOpen(true)}
                data-cy="po-upload-open"
              >
                Upload purchase order
              </Button>
            )}
          </>
        }
      />

      <PurchaseOrdersList emptyMessage="No purchase orders yet — they appear here when a reseller sends one or staff upload one." />

      {canEdit && (
        <PurchaseOrderDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onSaved={(po) => {
            setUploadOpen(false)
            toast({ title: "Purchase order uploaded", description: `${po.number} was recorded.` })
            navigate(`/crm/purchase-orders/${po.id}`)
          }}
        />
      )}
    </Box>
  )
}
